import { ApiClient } from '@revolugo/booking-api-client'
import { ApiVersion, Environment } from '@revolugo/common/constants'
import { omit } from 'lodash-es'

import type { IBookingBackend } from '../base/booking.ts'
import type { IHotelRoomingListGuestsBackend } from '../base/hotel-rooming-list-guest.ts'
import type { IHotelRoomingListsBackend } from '../base/hotel-rooming-list.ts'
import type { IHotelsBackend } from '../base/hotel.ts'
import type { IRevolugoService } from '../index.ts'
import type { ApiClientConfig, ApiVersions } from '@revolugo/common/types'
import type { PiniaPlugin } from 'pinia'

export class BookingApiClient<T extends ApiVersions>
  implements IRevolugoService
{
  public bookingService: IBookingBackend
  public hotelsService: IHotelsBackend
  public hotelRoomingListsService: IHotelRoomingListsBackend
  public hotelRoomingListGuestsService: IHotelRoomingListGuestsBackend

  constructor(apiClient: ApiClient<T>) {
    this.bookingService = apiClient.bookings
    this.hotelsService = apiClient.hotels
    this.hotelRoomingListsService = apiClient.hotelRoomingLists
    this.hotelRoomingListGuestsService = apiClient.hotelRoomingListGuests
  }
}

export default function bookingApiPlugin(config: {
  apiEnvironment: Environment
  apiKey: string
  organizationId?: string
  optionalHeaders?: Record<string, string>
}): PiniaPlugin {
  return () => {
    const apiClientConfig: ApiClientConfig<ApiVersion.V1> = {
      apiEnvironment: config.apiEnvironment,
      apiKey: config.apiKey,
      apiVersion: ApiVersion.V1,
    }

    const optionalHeaders: Record<string, string> = {}

    if (config.organizationId) {
      optionalHeaders['x-custom-organization'] = config.organizationId
    }

    apiClientConfig.optionalHeaders = Object.assign(
      optionalHeaders,
      config.apiEnvironment === Environment.Local
        ? config.optionalHeaders
        : omit(config.optionalHeaders, 'x-consumer-custom-id'),
    )

    return { apiClient: new BookingApiClient(new ApiClient(apiClientConfig)) }
  }
}
